<template>
  <div class="data-display-wrapper">
    <Spin v-if="loading" fix>
      <div class="loading-text">
        <Icon type="ios-loading" size="30" class="spin-icon-load"></Icon>
        <div>{{ langMap.dashboard_loading[lang] }}</div>
      </div>
    </Spin>
    
    <div v-if="!loading && !hasData" class="empty-state">
      <div class="empty-icon-wrapper">
        <div class="hologram-ring"></div>
        <div class="hologram-ring delay-1"></div>
        <div class="hologram-ring delay-2"></div>
        <div class="empty-icon">🔔</div>
      </div>
      <div class="empty-text">{{ langMap.empty[lang] }}</div>
      <div class="empty-subtext">{{ emptyMessage }}</div>
    </div>
    
    <div v-if="!loading && hasData" class="data-content">
      <div class="table-wrapper">
        <Table
          :columns="columns"
          :data="tableData"
          stripe
          border
          size="small"
          :height="tableHeight"
        >
          <template slot-scope="{ row }" slot="action">
            <span
              :class="['emergency-action-tag', `action-${actionMeta(row.action).variant}`]"
              :title="actionLabel(row.action)"
            >
              <span class="action-tag-icon" aria-hidden="true">{{ actionMeta(row.action).icon }}</span>
              <span>{{ actionLabel(row.action) }}</span>
            </span>
          </template>
          <template slot-scope="{ row }" slot="reason">
            <span class="emergency-reason-text">{{ reasonLabel(row) }}</span>
          </template>
          <template slot-scope="{ row }" slot="alertType">
            <span :class="['alert-type-tag', getAlertTypeClass(row.type)]">
              {{ getAlertTypeText(row.type) }}
            </span>
          </template>
        </Table>
      </div>
      
      <div class="pagination-wrapper">
        <div class="cyber-pagination">
          <div class="pagination-info">
            <span class="info-label">{{ langMap.dashboard_totalRecords[lang] }}</span>
            <span class="info-value">{{ pagination.total }}</span>
            <span class="info-divider">|</span>
            <span class="info-text">{{ pageRangeText }}</span>
          </div>
          
          <div class="pagination-controls">
            <button 
              class="page-btn page-first"
              :disabled="pagination.current === 1"
              @click="handlePageChange(1)"
            >
              <span class="btn-icon">《《</span>
            </button>
            
            <button 
              class="page-btn page-prev"
              :disabled="pagination.current === 1"
              @click="handlePageChange(pagination.current - 1)"
            >
              <span class="btn-icon">‹</span>
            </button>
            
            <div class="page-numbers">
              <button
                v-for="page in visiblePages"
                :key="page"
                :class="['page-number', { active: page === pagination.current, ellipsis: page === '...' }]"
                :disabled="page === '...'"
                @click="page !== '...' && handlePageChange(page)"
              >
                <span class="number-glow" v-if="page === pagination.current"></span>
                {{ page }}
              </button>
            </div>
            
            <button 
              class="page-btn page-next"
              :disabled="pagination.current === totalPages"
              @click="handlePageChange(pagination.current + 1)"
            >
              <span class="btn-icon">›</span>
            </button>
            
            <button 
              class="page-btn page-last"
              :disabled="pagination.current === totalPages"
              @click="handlePageChange(totalPages)"
            >
              <span class="btn-icon">》》</span>
            </button>
          </div>
          
          <div class="page-size-selector">
            <span class="selector-label">{{ langMap.dashboard_perPage[lang] }}</span>
            <div class="size-options">
              <button
                v-for="size in pageSizeOptions"
                :key="size"
                :class="['size-option', { active: pagination.size === size }]"
                @click="handlePageSizeChange(size)"
              >
                {{ size }}
              </button>
            </div>
          </div>
          
          <div class="page-jumper">
            <span class="jumper-label">{{ langMap.dashboard_jumpTo[lang] }}</span>
            <input 
              type="number" 
              class="jumper-input"
              v-model.number="jumpPage"
              @keyup.enter="handleJumpPage"
              :placeholder="String(pagination.current)"
              min="1"
              :max="totalPages"
            />
            <button class="jumper-btn" @click="handleJumpPage">
              <span class="btn-arrow">→</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import langMap from '@/utils/language'
import { mapState } from 'vuex';

const emergencyActionMeta = action => {
  if (action === 'stop_moving') {
    return { variant: 'stop', icon: '!' };
  }
  if (action === 'stop_moving_recover') {
    return { variant: 'recover', icon: '✓' };
  }
  return { variant: 'unknown', icon: '?' };
};

export default {
  name: 'EmergencyHistoryTable',
  props: {
    loading: { type: Boolean, default: false },
    hasData: { type: Boolean, default: false },
    emptyMessage: { type: String, default: '' },
    tableData: { type: Array, default: () => [] },
    pagination: { type: Object, default: () => ({ total: 0, current: 1, size: 20, pages: 0 }) },
    historyType: { type: String, default: 'emergency' }
  },
  data() {
    return {
      langMap: langMap,
      pageSizeOptions: [10, 20, 30, 50],
      jumpPage: null,
      windowHeight: window.innerHeight,
      columns: []
    }
  },
  computed: {
    ...mapState({ lang: state => state.app.language }),
    tableHeight() {
      return Math.max(400, this.windowHeight - 502);
    },
    totalPages() {
      return Math.ceil(this.pagination.total / this.pagination.size) || 1;
    },
    pageRangeText() {
      const start = (this.pagination.current - 1) * this.pagination.size + 1;
      const end = Math.min(this.pagination.current * this.pagination.size, this.pagination.total);
      return `${start}-${end}`;
    },
    visiblePages() {
      const current = this.pagination.current;
      const total = this.totalPages;
      const pages = [];
      if (total <= 7) {
        for (let i = 1; i <= total; i++) pages.push(i);
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) pages.push(i);
          pages.push('...');
          pages.push(total);
        } else if (current >= total - 3) {
          pages.push(1);
          pages.push('...');
          for (let i = total - 4; i <= total; i++) pages.push(i);
        } else {
          pages.push(1);
          pages.push('...');
          for (let i = current - 1; i <= current + 1; i++) pages.push(i);
          pages.push('...');
          pages.push(total);
        }
      }
      return pages;
    }
  },
  watch: {
    lang() { this.updateColumnsTitle(); },
    historyType() { this.updateColumnsTitle(); }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    this.updateColumnsTitle();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    actionMeta(action) {
      return emergencyActionMeta(action);
    },
    actionLabel(action) {
      if (!action) return '-';
      return this.langMap[action] ? this.langMap[action][this.lang] : action;
    },
    reasonLabel(row) {
      if (!row || !row.reason) return '';
      const reasonKey = `${row.action || ''}_disconnection`;
      return this.langMap[reasonKey] ? this.langMap[reasonKey][this.lang] : row.reason;
    },
    updateColumnsTitle() {
      if (this.historyType === 'alert') {
        this.columns = [
          { title: this.langMap.dashboard_dataTime[this.lang], key: 'dataTime', width: 180, align: 'center' },
          { title: this.langMap.alertInfoType[this.lang], slot: 'alertType', align: 'center', minWidth: 160 },
          { title: this.langMap.dashboard_robotCode[this.lang], key: 'robotCode', width: 150, align: 'center' }
        ];
      } else {
        this.columns = [
          { title: this.langMap.dashboard_dataTime[this.lang], key: 'createTime', width: 180, align: 'center' },
          { title: this.langMap.action[this.lang], slot: 'action', align: 'center', minWidth: 150 },
          { title: this.langMap.reason[this.lang], slot: 'reason', align: 'center', minWidth: 200 },
          { title: this.langMap.dashboard_robotCode[this.lang], key: 'robotCode', width: 150, align: 'center' }
        ];
      }
    },
    getAlertTypeText(type) {
      const typeMap = {
        1: 'alertInfoObstacleAvoidanceFailed',
        2: 'alertInfoBehaviorProhibitionFailed'
      };
      return typeMap[type] ? this.langMap[typeMap[type]][this.lang] : (type || '-');
    },
    getAlertTypeClass(type) {
      const numType = Number(type);
      if (numType === 1) return 'type-obstacle';
      if (numType === 2) return 'type-prohibition';
      return 'type-default';
    },
    formatTime(timestamp) {
      if (!timestamp) return '-';
      const date = new Date(timestamp);
      return date.toLocaleString();
    },
    handlePageChange(page) { this.$emit('page-change', page); },
    handlePageSizeChange(size) { this.$emit('size-change', size); },
    handleJumpPage() {
      if (!this.jumpPage || this.jumpPage < 1 || this.jumpPage > this.totalPages) {
        this.$Message.warning(this.langMap.dashboard_inputValidPageRange[this.lang].replace('{max}', this.totalPages));
        return;
      }
      this.handlePageChange(this.jumpPage);
      this.jumpPage = null;
    },
    handleResize() { this.windowHeight = window.innerHeight; }
  }
}
</script>

<style lang="less" scoped>
.data-display-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.emergency-action-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 86px;
  min-height: 24px;
  padding: 2px 10px 2px 5px;
  border: 1px solid transparent;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  letter-spacing: 0.2px;
  white-space: nowrap;
  transition: all 0.25s ease;

  .action-tag-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    font-size: 12px;
    font-weight: 800;
    line-height: 1;
  }

  &.action-stop {
    color: #ff8b8b;
    background: linear-gradient(135deg, rgba(255, 64, 64, 0.2), rgba(183, 28, 28, 0.12));
    border-color: rgba(255, 99, 99, 0.7);
    box-shadow: 0 0 10px rgba(255, 64, 64, 0.2);

    .action-tag-icon {
      color: #fff;
      background: #ef5350;
      box-shadow: 0 0 7px rgba(255, 82, 82, 0.7);
    }
  }

  &.action-recover {
    color: #76f2b2;
    background: linear-gradient(135deg, rgba(0, 200, 130, 0.18), rgba(0, 120, 90, 0.1));
    border-color: rgba(57, 220, 145, 0.68);
    box-shadow: 0 0 10px rgba(0, 220, 140, 0.18);

    .action-tag-icon {
      color: #003d2c;
      background: #5af0a5;
      box-shadow: 0 0 7px rgba(90, 240, 165, 0.6);
    }
  }

  &.action-unknown {
    color: #8edcff;
    background: rgba(0, 180, 255, 0.12);
    border-color: rgba(0, 200, 255, 0.48);

    .action-tag-icon {
      color: #002d40;
      background: #72ddff;
    }
  }

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.12);
  }
}

.emergency-reason-text {
  color: #9bdcff;
  font-weight: 500;
  letter-spacing: 0.15px;
  text-shadow: 0 0 8px rgba(95, 207, 255, 0.24);
}

.alert-type-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 104px;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.2px;
  border: 1px solid transparent;
}

.type-obstacle,
.type-prohibition {
  color: #ff8b8b;
  background: rgba(255, 64, 64, 0.16);
  border-color: rgba(255, 99, 99, 0.58);
}

.type-default {
  color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.24);
}
</style>
